import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Pencil, Trash2, Eye, Plus, Search, User, Filter, FileText, Crown, AlertCircle } from "lucide-react";
import { PatientFormSheet } from "@/components/patients/PatientFormSheet";
import { PageTitle } from "@/components/PageTitle";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";
import { formatCPF } from "@/lib/formatters";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

type Patient = {
  id: string;
  full_name: string;
  cpf: string;
  celular?: string | null;
  email?: string | null;
  data_nascimento?: string | null;
  sexo?: string | null;
  created_at: string;
  avatar_url?: string | null;
};

export default function PatientsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState<Patient | null>(null);

  const subscriptionStatus = useSubscriptionStatus();
  const refreshPatientsCount = useSubscriptionStore((state) => state.refreshPatientsCount);
  const { patientsCount } = useSubscriptionStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");

  async function loadPatients() {
    try {
      setLoading(true);
      const { data, error } = await supabase.from("patients_onboarding")
        .select("id, full_name, cpf, celular, email, data_nascimento, sexo, created_at, avatar_url")
        .order("full_name", { ascending: true });
      if (error) throw new Error(error.message);
      setRows(data || []);
    } catch (e: any) {
      setErr(e instanceof Error ? e.message : "Falha ao carregar pacientes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPatients();
  }, []);

  // Fechar modal de upgrade automaticamente quando puder adicionar paciente
  useEffect(() => {
    if (showUpgradeModal && subscriptionStatus.canAddPatients) {
      setShowUpgradeModal(false);
    }
  }, [showUpgradeModal, subscriptionStatus.canAddPatients]);


  function onDelete(patient: Patient) {
    setDeletingPatient(patient);
  }

  async function confirmDelete() {
    if (!deletingPatient) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        'delete-patient-cascade',
        { body: { patientIds: [deletingPatient.id] } }
      );

      if (error) throw error;

      setRows(prev => prev.filter(p => p.id !== deletingPatient.id));
      // Atualizar contagem de pacientes no store
      await refreshPatientsCount();
      // Atualizar subscriptionStatus
      if (subscriptionStatus.refresh) {
        await subscriptionStatus.refresh();
      }
      // Fechar modal de upgrade se estiver aberto, já que agora pode adicionar paciente
      setShowUpgradeModal(false);
    } catch (error: any) {
      console.error('Erro ao excluir paciente:', error);
      setErr(error.message);
    } finally {
      setDeletingPatient(null);
    }
  }

  // Filtrar pacientes
  const filteredPatients = useMemo(() => {
    let filtered = [...rows];

    // Filtro de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.full_name.toLowerCase().includes(term) ||
        p.cpf.includes(term) ||
        p.email?.toLowerCase().includes(term) ||
        p.celular?.includes(term)
      );
    }

    return filtered;
  }, [rows, searchTerm]);


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <PageTitle>Pacientes</PageTitle>
        <div className="flex gap-2 w-full sm:w-auto sm:justify-end">
          <Button
            onClick={() => {
              if (!subscriptionStatus.canAddPatients) {
                setShowUpgradeModal(true);
                return;
              }
              setOpenForm(true);
            }}
            className="inline-flex items-center gap-2 w-full sm:w-auto sm:order-1"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Paciente</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/app/pacientes/importar")}
            className="inline-flex items-center gap-2 w-full sm:w-auto sm:order-2"
          >
            <FileText className="h-4 w-4" />
            Importar
          </Button>
        </div>
      </div>

      {err && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">{err}</div>}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, CPF, email ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contador */}
      {!loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>
            {filteredPatients.length} {filteredPatients.length === 1 ? 'paciente encontrado' : 'pacientes encontrados'}
          </span>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Card key={index}>
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-8 rounded-md flex-shrink-0" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-end gap-2">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <Skeleton className="h-9 w-9 rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cards de Pacientes */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPatients.map((p) => (
            <Card
              key={p.id}
              className="hover:shadow-lg transition-all hover:scale-[1.02] duration-200 cursor-pointer"
              onClick={() => navigate(`/app/pacientes/${p.id}`)}
            >
              <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Avatar className="h-12 w-12 flex-shrink-0">
                        <AvatarImage src={p.avatar_url || undefined} alt={p.full_name} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          <User className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base leading-tight truncate" title={p.full_name}>
                        {p.full_name}
                      </h3>
                      {p.data_nascimento && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {(() => {
                            const today = new Date();
                            const birth = new Date(p.data_nascimento);
                            let age = today.getFullYear() - birth.getFullYear();
                            const monthDiff = today.getMonth() - birth.getMonth();
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                              age--;
                            }
                            return `${age} anos`;
                          })()}
                        </p>
                      )}
                      {p.cpf && (
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">
                          CPF: {formatCPF(p.cpf)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="flex items-center justify-end gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-primary/10 hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/pacientes/${p.id}`);
                    }}
                    title="Ver detalhes"
                  >
                    <Eye className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-blue-50 hover:text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/pacientes/${p.id}/editar`);
                    }}
                    title="Editar"
                  >
                    <Pencil className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(p);
                    }}
                    title="Excluir"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredPatients.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              {searchTerm
                ? "Nenhum paciente encontrado com os filtros aplicados"
                : "Nenhum paciente cadastrado"
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </CardContent>
        </Card>
      )}

      <PatientFormSheet
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={loadPatients}
        patient={null}
      />

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={!!deletingPatient} onOpenChange={() => setDeletingPatient(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o paciente <strong>{deletingPatient?.full_name}</strong>?
              <br />
              <br />
              Esta ação é irreversível e todos os dados associados ao paciente serão permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Limite Atingido */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason="patients"
        currentCount={patientsCount}
      />
    </div>
  );
}
